import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute } from '@/app/constant';
import { Modal } from '@/components/Modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMy } from '@/hooks/useMy';
import {
  type PlextracReportInfo,
  usePushToPlexTrac,
} from '@/hooks/usePlextrac';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import type { DropdownOption } from '@/types/dropdown.types';
import { generatePathWithSearch } from '@/utils/url.util';

interface PushToPlexTracModalProps {
  isOpen: boolean;
  onClose: () => void;
  riskKey: string;
  riskName: string;
}

export function PushToPlexTracModal({
  isOpen,
  onClose,
  riskKey,
}: PushToPlexTracModalProps) {
  const { drawerState } = useGlobalState();
  const navigate = useNavigate();
  const [selectedReport, setSelectedReport] = useState<DropdownOption | null>(
    null
  );

  const { isPraetorianUser } = useAuth();

  // Fetch connected reports from Configuration objects
  const { data: connectedReportsData, status: reportsStatus } = useMy(
    {
      resource: 'configuration',
      query: 'plextrac#report',
    },
    {
      enabled: isOpen,
    }
  );

  const { mutateAsync: pushToPlexTrac, status: pushToPlexTracStatus } =
    usePushToPlexTrac();

  // Transform Configuration objects to dropdown options
  const reportOptions = useMemo(() => {
    if (!connectedReportsData || connectedReportsData.length === 0) {
      return [];
    }

    return (
      connectedReportsData
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((config: any) => {
          // Extract report ID from key like "#configuration#plextrac#report-936718458"
          const match = config.key?.match(/plextrac#report-(\d+)/);
          if (!match) return null;

          const reportId = match[1];

          // Value is now a ReportInfo struct with full report details
          const reportInfo = config.value as PlextracReportInfo;
          const reportName = reportInfo?.name
            ? `${reportId} - ${reportInfo.name}`
            : `${reportId}`;

          return {
            value: reportId,
            label: reportName,
          } as DropdownOption;
        })
        .filter((option): option is DropdownOption => option !== null)
    );
  }, [connectedReportsData]);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedReport(null);
    }
  }, [isOpen]);

  const handlePush = async () => {
    if (!selectedReport) {
      toast.error('Please select a report');
      return;
    }
    await pushToPlexTrac({ riskKey, reportID: Number(selectedReport?.value) });
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      title="Push Finding to PlexTrac Report"
      onClose={onClose}
      size="lg"
      footer={{
        className: 'w-full',
        text:
          pushToPlexTracStatus === 'pending'
            ? 'Pushing...'
            : 'Push to PlexTrac',
        onClick: handlePush,
        disabled:
          reportsStatus === 'pending' ||
          pushToPlexTracStatus === 'pending' ||
          !selectedReport,
        'data-testid': 'plextrac-push-button',
      }}
    >
      <div className="space-y-4">
        <div data-testid="plextrac-report-dropdown">
          <Select
            value={(selectedReport?.value as string | undefined) ?? ''}
            onValueChange={value => {
              const option = reportOptions.find(o => o.value === value);
              setSelectedReport(option ?? null);
            }}
            disabled={reportsStatus === 'pending' || reportOptions.length === 0}
          >
            <SelectTrigger
              aria-label="Select report"
              className="flex w-full justify-between"
            >
              <SelectValue
                placeholder={
                  reportsStatus === 'pending'
                    ? 'Loading connected reports...'
                    : reportOptions.length === 0
                      ? 'No connected reports'
                      : 'Select Report'
                }
              />
            </SelectTrigger>
            <SelectContent className="w-full">
              {reportOptions.map(option => (
                <SelectItem key={option.value} value={option.value as string}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {reportOptions.length === 0 && reportsStatus === 'success' && (
          <div className="border-chariot-stroke-medium bg-chariot-status-info/10 text-chariot-text-primary flex gap-1 rounded border p-2 text-sm">
            <InformationCircleIcon className="text-chariot-status-info mr-1 inline size-5 shrink-0" />
            <button
              type="button"
              className="focus-visible:ring-ring cursor-pointer text-left focus-visible:ring-1 focus-visible:outline-hidden"
              onClick={() => {
                onClose();
                drawerState.handleOpenVulnerabilityDrawer();
                // Navigate to the page only — do NOT hand-write drawer params
                // here (ENG-5571). All drawerOrder / <type>DrawerKey writes are
                // owned by the global.state handlers, which are cap-guarded and
                // set the in-memory drawer object that the render gate in
                // sections/drawers/index.tsx actually reads. A URL-only write
                // left a `customer` token that rendered nothing, yet still
                // consumed one of MAX_OPEN_DRAWERS and disabled global hotkeys
                // for the rest of the session (the orphan prune is mount-only).
                navigate(AuthenticatedRoute.CUSTOMER_MANAGEMENT);
              }}
            >
              {`No connected reports found. Please connect a report in the ${isPraetorianUser ? 'Customer Management' : 'Tenant Management'} settings.`}
            </button>
          </div>
        )}
        {reportsStatus === 'error' && (
          <p className="border-chariot-stroke-medium bg-chariot-status-medium text-chariot-text-primary flex gap-1 rounded border p-2 text-sm">
            <ExclamationTriangleIcon className="text-chariot-text-primary mr-1 inline size-5 shrink-0" />
            <div
              className="cursor-pointer"
              onClick={() => {
                onClose();
                drawerState.handleOpenVulnerabilityDrawer();
                navigate(
                  generatePathWithSearch({
                    pathname: AuthenticatedRoute.SETTINGS,
                    appendSearch: [['tab', 'scanSettings']],
                  })
                );
              }}
            >
              {`Failed to load connected reports. Please check your PlexTrac configuration under Settings > Scan Settings > Reporting.`}
            </div>
          </p>
        )}
      </div>
    </Modal>
  );
}
